import { GenericEnum, IFieldDataOptions, IOptionData } from 'interfaces/utils';
import {
  ISystemAndSubSystem,
  Parameters,
  ParametersResponse,
} from './interfaces';

export const parseParametersResponse = (
  data: ParametersResponse,
): Parameters => {
  return {
    ...data,
    serviceCategoriesEnums: getServiceCategoriesEnums(data.serviceCategories),
    remunerationPeriodicityEnums: getEnums(data.remunerationPeriodicityTypes),
    fixedIndicatorOptionsByType: getFixedIndicatorOptionsByType(
      data.remunerationFixedTypes,
    ),
    fixedIndicatorFieldNamesByType: getFixedIndicatorFieldNamesByType(
      data.remunerationFixedTypes,
    ),
    fixedIndicatorLabelByType: getFixedIndicatorLabelByType(
      data.remunerationFixedTypes,
    ),
    fixedIndicatorFields: getFixedIndicatorOptionsFields(
      data.remunerationFixedTypes,
    ),
    systemsKeys: data.systems.map((system) => system.value),
    systemsEnums: getValueLabel(data.systems),

    mailingRuleStatusEnums: getValueLabel(data.mailingRuleStatus),
    mailingRuleSystemFields: getMailingRuleSystemFields(
      data.mailingRuleSystems,
    ),
    mailingRuleSystemsValue: getMailingRuleSystemsValue(
      data.mailingRuleSystems,
    ),
    mailingRuleSubSystemValuesOrderBySystem: getMailingRuleSubSystemValuesOrderBySystem(
      data.mailingRuleSystems,
    ),
    mailingRuleSubSystemOptionBySystem: getMailingRuleSubSystemOptionBySystem(
      data.mailingRuleSystems,
    ),
    mailingRuleSystemAndSubSystemByValue: getMailingRuleSystemAndSubSystemByValue(
      data.mailingRuleSystems,
    ),
    strategyStatusEnum: getValueLabel(data.strategyStatus),
  };
};

const getServiceCategoriesEnums = (
  serviceCategoires: Array<{
    _id: string;
    description: string;
  }>,
): GenericEnum => {
  return serviceCategoires.reduce((all, current) => {
    all[current._id] = current.description;
    return all;
  }, {} as GenericEnum);
};

const getMailingRuleSystemAndSubSystemByValue = (
  mailingRuleSystems: Array<IFieldDataOptions>,
): { [key: string]: ISystemAndSubSystem } =>
  mailingRuleSystems.reduce((all, system) => {
    system.fieldOptions.forEach((subSystem) => {
      if (subSystem.fieldOptions) {
        subSystem.fieldOptions.forEach((item) => {
          if (item.fieldOptions) {
            item.fieldOptions.forEach((item2) => {
              all[item2.value] = {
                system: system.fieldName,
                subSystem: item2.value,
              };
            });
          } else {
            all[item.value] = {
              system: system.fieldName,
              subSystem: item.value,
            };
          }
        });
      } else {
        all[subSystem.value] = {
          system: system.fieldName,
          subSystem: subSystem.value,
        };
      }
    });
    return all;
  }, {} as { [key: string]: ISystemAndSubSystem });

const getMailingRuleSubSystemOptionBySystem = (
  mailingRuleSystems: Array<IFieldDataOptions>,
): { [key: string]: IFieldDataOptions } =>
  mailingRuleSystems.reduce((all, system) => {
    all[system.fieldName] = system;
    return all;
  }, {} as { [key: string]: IFieldDataOptions });

const getAllChildrenValues = (options) => {
  const childrenValues = [];
  options.forEach(({ value, fieldOptions }) => {
    if (fieldOptions) {
      childrenValues.push(...getAllChildrenValues(fieldOptions));
    } else {
      childrenValues.push(value);
    }
  });
  return childrenValues;
};

const getMailingRuleSubSystemValuesOrderBySystem = (
  mailingRuleSystems: Array<IFieldDataOptions>,
): { [key: string]: string[] } =>
  mailingRuleSystems.reduce((all, system) => {
    all[system.fieldName] = getAllChildrenValues(system.fieldOptions);
    return all;
  }, {} as { [key: string]: string[] });

const getMailingRuleSystemsValue = (
  mailingRuleSystems: Array<IFieldDataOptions>,
): { [key: string]: string } =>
  mailingRuleSystems.reduce((all, system) => {
    all[system.fieldName] = system.value;
    return all;
  }, {} as { [key: string]: string });

const getMailingRuleSystemFields = (
  mailingRuleSystems: Array<IFieldDataOptions>,
): Array<string> => mailingRuleSystems.map((system) => system.fieldName);

const getValueLabel = (
  optionData: Array<IOptionData | IFieldDataOptions>,
): { [key: string]: string } =>
  optionData.reduce((all, { value, label }) => {
    all[value] = label;
    return all;
  }, {} as { [key: string]: string });

const getEnums = (
  optionData: Array<IOptionData | IFieldDataOptions>,
): { [key: string]: string } =>
  optionData.reduce((all, { value }) => {
    all[value] = value;
    return all;
  }, {} as { [key: string]: string });

const getFixedIndicatorOptionsByType = (
  fieldData: IFieldDataOptions[],
): { [key: string]: IFieldDataOptions[] } => {
  return fieldData.reduce((all, current) => {
    const listOfOptions = current.fieldOptions?.map((option) => option) || [];
    all[current.value] = listOfOptions;
    return all;
  }, {});
};

const getFixedIndicatorFieldNamesByType = (
  fieldData: IFieldDataOptions[],
): { [key: string]: string[] } => {
  return fieldData.reduce((all, current) => {
    const listOfFieldNames =
      current.fieldOptions?.map((option) => option.fieldName) || [];
    all[current.value] = listOfFieldNames;
    return all;
  }, {});
};

const getFixedIndicatorLabelByType = (
  fieldData: IFieldDataOptions[],
): { [key: string]: string } => {
  return fieldData.reduce((all, current) => {
    all[current.value] = current.label;
    return all;
  }, {});
};

const getFixedIndicatorOptionsFields = (
  fieldData: IFieldDataOptions[],
): string[] => {
  return Object.keys(
    fieldData.reduce((all, current) => {
      current.fieldOptions?.forEach((option) => (all[option.fieldName] = 0));
      return all;
    }, {}),
  );
};
